// ===== Convert tab UI =====
// Drives the ad-network converter (convert-core.js) from two input sources:
// local files (picker/drag-drop) and uploaded creatives (workbench DB/R2).

import { escapeHtml, fmtSize } from './util.js';
import { NETWORKS, convert, makeZip, utf8WithBom, outName } from './convert-core.js';
import { CONVERT_ICON, renderNetworkPicker, renderCard, wireResultCards, downloadAll } from './convert-ui.js';

const root = document.getElementById('convertRoot');

// Input items queued for conversion. Each: { id, name, getText() -> Promise<string>, meta }
// Convert handles local files only — saved canvases are converted via the Download tab.
let inputs = [];
// Starts empty so the network-then-file order actually holds — defaulting to
// all would leave the dropzone unlocked from the first paint and the ordering
// would be decoration. "전체" keeps the common case to one click.
let selectedNetworks = new Set();
let inputSeq = 0;

// ===== Top-level render =====
function render() {
  root.innerHTML = `
    <div class="cv-head">
      <h2>네트워크 변환</h2>
      <p>Unity Playworks 플레이어블 HTML을 각 광고 네트워크 규격으로 변환합니다. (저장된 캔버스는 Download 탭에서 변환하세요.)</p>
    </div>

    <div class="cv-section">
      <div class="cv-section-label cv-label-row">
        <span><b>1</b> 변환 대상 네트워크</span>
        <button class="cv-link" id="cvAllNets"></button>
      </div>
      <div class="cv-networks" id="cvNetworks"></div>
    </div>

    <div class="cv-section">
      <div class="cv-section-label"><b>2</b> 원본 HTML</div>
      <div class="cv-source-body" id="cvSourceBody"></div>
    </div>

    <div class="cv-actions">
      <button class="primary" id="cvConvertBtn">${CONVERT_ICON}<span>변환</span></button>
      <span class="cv-hint" id="cvHint"></span>
    </div>

    <div class="cv-results" id="cvResults"></div>
  `;

  renderNetworkPicker(root.querySelector('#cvNetworks'), selectedNetworks, onNetworksChange);
  renderSourceBody();
  renderResults([]);

  root.querySelector('#cvAllNets').addEventListener('click', () => {
    if (selectedNetworks.size === NETWORKS.length) selectedNetworks.clear();
    else NETWORKS.forEach(n => selectedNetworks.add(n.key));
    renderNetworkPicker(root.querySelector('#cvNetworks'), selectedNetworks, onNetworksChange);
    onNetworksChange();
  });

  root.querySelector('#cvConvertBtn').addEventListener('click', runConvert);
  onNetworksChange();
}

// ===== Source: local files =====
function renderSourceBody() {
  const body = root.querySelector('#cvSourceBody');
  body.innerHTML = `
    <div class="cv-drop" id="cvDrop">
      <input type="file" id="cvFileInput" accept=".html,.htm" multiple hidden>
      <div class="cv-drop-inner">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
        <div class="cv-drop-text">HTML 파일을 끌어다 놓거나 <button class="cv-link" id="cvBrowse">찾아보기</button></div>
        <div class="cv-drop-sub">.html · 여러 개 선택 가능</div>
        <div class="cv-drop-locked">먼저 변환 대상 네트워크를 선택하세요</div>
      </div>
    </div>
    <div class="cv-filelist" id="cvFileList"></div>
  `;
  const drop = body.querySelector('#cvDrop');
  const fileInput = body.querySelector('#cvFileInput');
  const openPicker = () => { if (selectedNetworks.size) fileInput.click(); };
  body.querySelector('#cvBrowse').addEventListener('click', (e) => { e.preventDefault(); openPicker(); });
  drop.addEventListener('click', (e) => {
    // The file input sits inside the dropzone, so the click openPicker() fires
    // on it bubbles back here — ignore it rather than re-entering.
    if (e.target === fileInput || e.target.closest('button')) return;
    openPicker();
  });
  fileInput.addEventListener('change', (e) => { addLocalFiles(e.target.files); e.target.value = ''; });
  ['dragenter', 'dragover'].forEach(ev =>
    drop.addEventListener(ev, (e) => {
      e.preventDefault();
      if (selectedNetworks.size) drop.classList.add('drag');
    }));
  ['dragleave', 'drop'].forEach(ev =>
    drop.addEventListener(ev, (e) => {
      e.preventDefault();
      if (ev === 'dragleave' && drop.contains(e.relatedTarget)) return;
      drop.classList.remove('drag');
    }));
  drop.addEventListener('drop', (e) => {
    e.preventDefault();
    if (selectedNetworks.size) addLocalFiles(e.dataTransfer.files);
  });
  renderFileList();
  syncDropState();
}

// Files can't be taken before a target exists — the conversion is defined by
// the network, so the picker leads and the dropzone unlocks behind it.
function syncDropState() {
  const drop = root.querySelector('#cvDrop');
  if (drop) drop.classList.toggle('locked', selectedNetworks.size === 0);
}

function onNetworksChange() {
  syncDropState();
  const all = root.querySelector('#cvAllNets');
  if (all) all.textContent = selectedNetworks.size === NETWORKS.length ? '전체 해제' : '전체 선택';
  updateHint();
}

function addLocalFiles(fileList) {
  for (const f of fileList) {
    if (!/\.html?$/i.test(f.name)) continue;
    inputs.push({
      id: 'in_' + (++inputSeq),
      name: f.name,
      meta: fmtSize(f.size),
      getText: () => f.text(),
    });
  }
  renderFileList();
  updateHint();
}

function renderFileList() {
  const el = root.querySelector('#cvFileList');
  if (!el) return;
  if (inputs.length === 0) { el.innerHTML = ''; return; }
  el.innerHTML = inputs.map(i => `
    <div class="cv-chip" data-id="${i.id}">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
      <span class="cv-chip-name">${escapeHtml(i.name)}</span>
      <span class="cv-chip-meta">${escapeHtml(i.meta || '')}</span>
      <button class="cv-chip-x" data-id="${i.id}" title="제거">×</button>
    </div>
  `).join('');
  el.querySelectorAll('.cv-chip-x').forEach(b =>
    b.addEventListener('click', () => {
      inputs = inputs.filter(i => i.id !== b.dataset.id);
      renderFileList();
      updateHint();
    }));
}

function updateHint() {
  const hint = root.querySelector('#cvHint');
  if (!hint) return;
  const n = inputs.length, m = selectedNetworks.size;
  const btn = root.querySelector('#cvConvertBtn');
  if (n === 0 || m === 0) {
    hint.textContent = m === 0 ? '네트워크를 1개 이상 선택하세요' : '파일을 추가하세요';
    if (btn) btn.disabled = true;
  } else {
    hint.textContent = `${n}개 파일 × ${m}개 네트워크 = ${n * m}개 변환`;
    if (btn) btn.disabled = false;
  }
}

// ===== Convert run =====
async function runConvert() {
  const btn = root.querySelector('#cvConvertBtn');
  btn.disabled = true;
  const results = [];
  const nets = NETWORKS.filter(n => selectedNetworks.has(n.key));

  for (const input of inputs) {
    let text;
    try {
      text = await input.getText();
    } catch (err) {
      for (const net of nets) {
        results.push({ ok: false, inputName: input.name, network: net, error: err.message });
      }
      continue;
    }
    for (const net of nets) {
      try {
        const { content, log } = convert(text, net.key);
        // UTF-8 with BOM, matching the .bat's PowerShell output byte-for-byte.
        const bytes = utf8WithBom(content);
        const htmlName = outName(input.name, net, 'html');
        const zipName = outName(input.name, net, 'zip');
        const htmlBlob = new Blob([bytes], { type: 'text/html' });
        const zipBlob = await makeZip([{ name: 'index.html', data: bytes }]);
        results.push({ ok: true, inputName: input.name, network: net, htmlName, zipName, htmlBlob, zipBlob, size: bytes.length, log });
      } catch (err) {
        results.push({ ok: false, inputName: input.name, network: net, error: err.message });
      }
    }
  }

  renderResults(results);
  btn.disabled = false;
  updateHint();
}

// ===== Results =====
function renderResults(results) {
  const el = root.querySelector('#cvResults');
  if (!el) return;
  if (!results.length) { el.innerHTML = ''; return; }

  const okOnes = results.filter(r => r.ok);
  el.innerHTML = `
    <div class="cv-results-head">
      <span>결과 · ${results.length}개 (성공 ${okOnes.length} / 실패 ${results.length - okOnes.length})</span>
      ${okOnes.length ? `<button id="cvDlAll">모두 다운로드 (HTML+ZIP)</button>` : ''}
    </div>
    <div class="cv-result-cards">
      ${results.map((r, idx) => renderCard(r, idx)).join('')}
    </div>
  `;

  wireResultCards(el, results);
  const dlAll = el.querySelector('#cvDlAll');
  if (dlAll) dlAll.addEventListener('click', () => downloadAll(okOnes));
}

// ===== Sub-tabs (네트워크 변환 / 빌드 검증) =====
// The Convert tab hosts two panels; this only swaps which one is visible.
// Each panel's module does its own lazy render (see below and verify.js).
document.querySelectorAll('#cvSubtabs .cv-subtab').forEach(btn => {
  btn.addEventListener('click', () => {
    const target = btn.dataset.cvSubtab;
    document.querySelectorAll('#cvSubtabs .cv-subtab').forEach(b =>
      b.classList.toggle('active', b === btn));
    document.querySelectorAll('.cv-subpanel').forEach(p =>
      p.classList.toggle('active', p.dataset.cvSubpanel === target));
  });
});

// Lazy init — build the Convert UI on first tab open, not at startup.
let cvInited = false;
function initConvert() {
  if (cvInited) return;
  cvInited = true;
  render();
}
document.querySelector('#homeTabs a[data-home-tab="convert"]')?.addEventListener('click', initConvert);
document.querySelector('#cvSubtabs .cv-subtab[data-cv-subtab="convert"]')?.addEventListener('click', initConvert);
